/**
 * Renders a crop selection to a data URL.
 *
 * Split out of the uploader so the geometry is testable on its own: the part that
 * goes wrong in a crop is the arithmetic, not the markup, and `react-easy-crop`
 * hands back a rectangle in source-image pixels that still has to be drawn onto a
 * canvas of the target size.
 */
import { MAX_IMAGE_UPLOAD_BYTES } from "@shared/imageUploadLimits";

export type PixelCrop = { x: number; y: number; width: number; height: number };

/** The formats the server's magic-byte check accepts. See server/memberPhoto.ts. */
const OUTPUT_MIME = "image/jpeg";

/**
 * JPEG rather than PNG for output.
 *
 * A 2550x3300 print crop as PNG is tens of megabytes and would fail the upload
 * ceiling on its own; the same image as a quality-0.9 JPEG is a couple. Photographs
 * are what these presets are for, and JPEG is what photographs should be.
 */
const OUTPUT_QUALITY = 0.9;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () => reject(new Error("That image could not be read.")));
    // Object URLs and data URLs are same-origin, so the canvas stays untainted and
    // `toDataURL` is allowed to read it back.
    image.src = src;
  });
}

/**
 * Draws `crop` from `src` into an image of `targetWidth` x `targetHeight`.
 *
 * When the target is null the crop's own pixel size is kept, which is what
 * free-form means. Either way the source rectangle fills the output exactly, so
 * nothing is letterboxed and nothing is stretched — the crop window was already
 * locked to the output ratio.
 */
export async function cropImageToDataUrl({
  src,
  crop,
  targetWidth,
  targetHeight,
}: {
  src: string;
  crop: PixelCrop;
  targetWidth: number | null;
  targetHeight: number | null;
}): Promise<string> {
  const image = await loadImage(src);

  const width = Math.max(1, Math.round(targetWidth ?? crop.width));
  const height = Math.max(1, Math.round(targetHeight ?? crop.height));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser could not prepare the image.");

  // A white base, because JPEG has no alpha: a transparent PNG cropped to JPEG
  // would otherwise composite onto black and come back with a dark background.
  context.fillStyle = "#FFFFFF";
  context.fillRect(0, 0, width, height);

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(
    image,
    Math.round(crop.x),
    Math.round(crop.y),
    Math.round(crop.width),
    Math.round(crop.height),
    0,
    0,
    width,
    height,
  );

  const dataUrl = canvas.toDataURL(OUTPUT_MIME, OUTPUT_QUALITY);

  /*
   * Checked here as well as on the server. The server's check is the control, but
   * failing at the point of cropping names the cause — an image too large to send
   * — while a rejected upload minutes later does not.
   */
  if (estimateDataUrlBytes(dataUrl) > MAX_IMAGE_UPLOAD_BYTES) {
    throw new Error("The cropped image is too large to upload. Try a smaller crop or a smaller source image.");
  }

  return dataUrl;
}

/**
 * The byte length a base64 data URL decodes to.
 *
 * Base64 carries 3 bytes in every 4 characters, and trailing `=` padding stands in
 * for bytes that are not there — so the padding is subtracted rather than counted.
 */
export function estimateDataUrlBytes(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}
